import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { hqApi } from "@/hq/hqApi";
import { toast } from "sonner";

// Phase 3I.4P.2 — Bulk Full Pitch modal. Hard-capped at 25 recipients per
// batch (enforced by both UI and API — see BULK_FULL_PITCH_HARD_CAP).
export default function BulkFullPitchModal({ open, businessIds, clientsById, onClose, onSent }) {
  const [confirmText, setConfirmText] = useState("");
  const [sending, setSending] = useState(false);
  const [preflight, setPreflight] = useState(null);       // dry-run result
  const [result, setResult] = useState(null);
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");

  const rows = useMemo(() => (businessIds || []).map((id) => clientsById[id]).filter(Boolean),
                       [businessIds, clientsById]);
  const overCap = rows.length > 25;

  useEffect(() => {
    if (!open) return;
    setConfirmText(""); setPreflight(null); setResult(null);
    setSubject(""); setIntro("");
    // Ask backend for a dry-run breakdown of who is eligible.
    hqApi.post("/control/clients/bulk-pitch/full", {
      business_ids: businessIds, dry_run: true,
    }).then((r) => setPreflight(r.data))
      .catch((e) => toast.error(e.response?.data?.detail?.message || "Failed to preflight"));
  }, [open, businessIds]);

  if (!open) return null;

  const eligible = (preflight?.results || []).filter((r) => r.status === "queued");
  const willSkip = (preflight?.results || []).filter((r) => r.status !== "queued");
  const ok = !overCap && !!preflight && confirmText.trim().toUpperCase() === "CONFIRM"
             && eligible.length > 0 && !sending;

  const send = async () => {
    setSending(true);
    try {
      const { data } = await hqApi.post("/control/clients/bulk-pitch/full", {
        business_ids: businessIds,
        subject: subject.trim() || undefined,
        intro:   intro.trim() || undefined,
        dry_run: false,
      });
      setResult(data);
      const label = data.sent_count === 1 ? "pitch" : "pitches";
      toast.success(`${data.sent_count} ${label} sent · ${data.skipped_count} skipped · ${data.failed_count} failed`);
      onSent?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Bulk send failed");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const StatusPill = ({ status }) => {
    const label = { queued: "Eligible", sent: "Sent", skipped: "Skipped", failed: "Failed" }[status] || status;
    const color = status === "sent" ? "bg-[#EBFBF2] text-[#0F7B3E] border-[#B7EBCF]"
              : status === "queued" ? "bg-[#FFF7EE] text-[#FF6A00] border-[#FCE1C7]"
              : status === "skipped" ? "bg-[#F6F5F2] text-[#8A8A8A] border-[#EAEAEA]"
              : "bg-[#FDECEC] text-[#C1350B] border-[#F5C6C6]";
    return <span className={`px-2 py-0.5 rounded-md text-[10px] font-black tracking-[0.06em] uppercase border ${color}`}>{label}</span>;
  };

  const REASON = {
    client_not_found:        "Client not found",
    client_archived:         "Client is archived",
    full_pitch_already_sent: "Full pitch already sent",
    no_email:                "No email on file",
    email_suppressed:        "Email suppressed / unsubscribed",
    internal_error:          "Internal error",
    dry_run:                 "Ready to send",
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="bulk-full-pitch-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Bulk Send</div>
            <div className="font-black text-lg">🍊 Full OrangeBySite Pitch — {rows.length} client{rows.length === 1 ? "" : "s"}</div>
          </div>
          <button onClick={onClose} data-testid="bulk-full-pitch-close" className="p-1 rounded hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>

        <div className="p-6 overflow-auto flex-1 space-y-5">
          {overCap && (
            <div className="p-3 rounded-lg bg-[#FDECEC] text-[#C1350B] text-sm border border-[#F5C6C6]" data-testid="bulk-full-pitch-over-cap">
              <b>{rows.length} clients selected</b> — the hard cap is 25 per batch. Reduce your selection to 25 or fewer.
            </div>
          )}

          {!result && preflight && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-[#FFF7EE] border border-[#FCE1C7]">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Eligible</div>
                  <div className="font-black text-2xl mt-1" data-testid="bulk-full-pitch-eligible-count">{eligible.length}</div>
                </div>
                <div className="p-3 rounded-lg bg-[#F6F5F2] border border-[#EAEAEA]">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#8A8A8A]">Will Skip</div>
                  <div className="font-black text-2xl mt-1" data-testid="bulk-full-pitch-skip-count">{willSkip.length}</div>
                </div>
                <div className="p-3 rounded-lg bg-[#FFFFFF] border border-[#EAEAEA]">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#8A8A8A]">Hard Cap</div>
                  <div className="font-black text-2xl mt-1">{preflight.hard_cap}</div>
                </div>
              </div>

              <details className="rounded-lg border border-[#EAEAEA]">
                <summary className="cursor-pointer px-4 py-2 text-[11px] tracking-[0.16em] uppercase font-black text-[#8A8A8A]">
                  Optional subject / intro override
                </summary>
                <div className="p-4 space-y-3">
                  <input value={subject} onChange={(e) => setSubject(e.target.value)}
                    placeholder="Custom subject (leave blank for default)"
                    data-testid="bulk-full-pitch-subject"
                    className="w-full px-3 py-2 rounded-lg border border-[#EAEAEA] text-[13px]"/>
                  <textarea value={intro} onChange={(e) => setIntro(e.target.value)}
                    placeholder="Custom intro paragraph (leave blank for default)"
                    rows={3}
                    data-testid="bulk-full-pitch-intro"
                    className="w-full px-3 py-2 rounded-lg border border-[#EAEAEA] text-[13px]"/>
                </div>
              </details>

              <div className="rounded-xl border border-[#EAEAEA] overflow-hidden" data-testid="bulk-full-pitch-list">
                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-[13px]">
                    <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.14em] uppercase font-black text-[#8A8A8A]">
                      <tr><th className="px-3 py-2 text-left">Client</th><th className="px-3 py-2 text-left">Email</th><th className="px-3 py-2 text-left">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-[#EAEAEA]">
                      {(preflight.results || []).map((r) => (
                        <tr key={r.business_id} data-testid={`bulk-row-${r.business_id}`}>
                          <td className="px-3 py-2 font-semibold">{r.business_name || clientsById[r.business_id]?.business_name || r.business_id.slice(0, 8)}</td>
                          <td className="px-3 py-2 text-[#4A4A4A]">{r.to_email || <span className="text-[#8A8A8A]">—</span>}</td>
                          <td className="px-3 py-2 whitespace-nowrap"><StatusPill status={r.status === "queued" ? "queued" : r.status}/>
                            {r.reason && r.status !== "queued" && (
                              <span className="ml-2 text-[11px] text-[#8A8A8A]">{REASON[r.reason] || r.reason}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="p-3 rounded-lg bg-[#0A0A0A] text-white text-sm">
                Type <b className="tracking-widest">CONFIRM</b> to send the Full OrangeBySite pitch to the {eligible.length} eligible client{eligible.length === 1 ? "" : "s"}.
                <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  data-testid="bulk-full-pitch-confirm"
                  placeholder="Type CONFIRM"
                  className="mt-2 w-40 px-3 py-1.5 rounded-md text-black text-[13px]"/>
              </div>
            </>
          )}

          {result && (
            <div className="space-y-3" data-testid="bulk-full-pitch-result">
              <div className="grid grid-cols-3 gap-3">
                <ResultTile label="Sent" value={result.sent_count} tone="green"/>
                <ResultTile label="Skipped" value={result.skipped_count} tone="grey"/>
                <ResultTile label="Failed" value={result.failed_count} tone="red"/>
              </div>
              <div className="text-[12px] text-[#8A8A8A]">Batch ID: {result.batch_id}</div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#EAEAEA] flex justify-end gap-2">
          <button onClick={onClose} data-testid="bulk-full-pitch-cancel"
            className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
            {result ? "Close" : "Cancel"}
          </button>
          {!result && (
            <button onClick={send} disabled={!ok} data-testid="bulk-full-pitch-send"
              className={`px-5 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${ok ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#EAEAEA] text-[#8A8A8A] cursor-not-allowed"}`}>
              {sending ? "Sending…" : `🍊 Send ${eligible.length} pitch${eligible.length === 1 ? "" : "es"}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultTile({ label, value, tone }) {
  const c = tone === "green" ? "bg-[#EBFBF2] text-[#0F7B3E] border-[#B7EBCF]"
        : tone === "red"    ? "bg-[#FDECEC] text-[#C1350B] border-[#F5C6C6]"
        :                     "bg-[#F6F5F2] text-[#4A4A4A] border-[#EAEAEA]";
  return (
    <div className={`p-3 rounded-lg border ${c}`}>
      <div className="text-[10px] tracking-[0.24em] uppercase font-black">{label}</div>
      <div className="font-black text-2xl mt-1">{value}</div>
    </div>
  );
}
